// src/components/Pentests/PentestDetail.js
import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useParams, NavLink } from 'react-router-dom';
import {
  Activity, ArrowDownRight, ArrowUpRight, BarChart3, Bot, Bug, ClipboardCheck, FileText, History, RefreshCw, Sparkles, Wrench, FlaskConical,
} from 'lucide-react';
import { pentestsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { HeroStrip } from '../common/Shinobi';

import PreEngagement from './tabs/PreEngagement';
import Tools from './tabs/Tools';
import Runs from './tabs/Runs';
import RunDetail from './RunDetail';
import Findings from './tabs/Findings';
import AIInsights from './tabs/AIInsights';
import AIPentest from './tabs/AIPentest/Index';
import Bench from './tabs/Bench/Index';
import Reports from './tabs/Reports';
import Retests from './tabs/Retests';
import Audit from './tabs/Audit';
import Dashboards from './tabs/Dashboards';

// pentesterOnly tabs are hidden in the nav AND blocked at the route level for
// Wasabian role (read-only viewers should not see operator-driving surfaces).
const TABS = [
  { path: 'dashboards',     label: 'Dashboards',     icon: BarChart3 },
  { path: 'pre-engagement', label: 'Pre-Engagement', icon: ClipboardCheck },
  { path: 'tools',          label: 'Tools',          icon: Wrench,   pentesterOnly: true },
  { path: 'runs',           label: 'Runs',           icon: History },
  { path: 'findings',       label: 'Findings',       icon: Bug },
  { path: 'ai-insights',    label: 'AI Insights',    icon: Sparkles },
  // Retired mode. Offered only where there is history to read: on everything
  // else the page's whole content was an explanation of why the page is
  // retired, which is a tab that exists to apologise for existing.
  { path: 'ai-pentest',     label: 'AI Pentest',     icon: Bot,      pentesterOnly: true,
    onlyIf: (pt) => (pt?.legacy_orchestrator_runs || 0) > 0 },
  // Path stays `bench` so anything bookmarked keeps working. The label is what
  // an operator looks for, and "Bench" containing a sub-tab also called Bench
  // was the confusing part.
  { path: 'bench',          label: 'Agents',         icon: FlaskConical, pentesterOnly: true },
  { path: 'reports',        label: 'Reports',        icon: FileText },
  { path: 'retests',        label: 'Retests',        icon: RefreshCw },
  { path: 'audit',          label: 'Audit',          icon: Activity },
];

const TYPE_LABELS = {
  external: 'External',
  internal: 'Internal',
  third_party_external: '3rd-party External',
  third_party_internal: '3rd-party Internal',
};

// Numeric ranking so "A" beats "B" beats "C" ... beats "F". Used by GradeDelta
// to decide whether the live grade is above/below the original snapshot.
const GRADE_RANK = { A: 5, B: 4, C: 3, D: 2, F: 1 };

function GradeDelta({ original, current }) {
  if (!original || !current || original === current) return null;
  const delta = (GRADE_RANK[current] || 0) - (GRADE_RANK[original] || 0);
  if (delta === 0) return null;
  const up = delta > 0;
  return (
    <span
      className={`ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${
        up
          ? 'bg-emerald-100 text-emerald-700 border-emerald-300'
          : 'bg-rose-100 text-rose-700 border-rose-300'
      }`}
      title={`Live grade is ${original} -> ${current} since the original delivery snapshot.`}
    >
      {up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
      from {original} to {current}
    </span>
  );
}

export default function PentestDetail() {
  const { id } = useParams();
  const { isPentester } = useAuth();
  const [pt, setPt] = useState(null);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    pentestsAPI
      .get(id)
      .then((data) => { if (!cancelled) setPt(data); })
      .catch((err) => { if (!cancelled) setError(err?.response?.data?.detail || err.message); });
    return () => { cancelled = true; };
  }, [id, reloadKey]);

  const onUpdated = (updated) => setPt(updated);
  const reload = () => setReloadKey((k) => k + 1);

  if (error) return <div className="p-8 text-red-600">Error: {error}</div>;
  if (!pt) return <div className="p-8 text-shinobi-green">Loading…</div>;

  // Wasabian (viewer) role hides operator-only surfaces from the tab nav AND
  // blocks the underlying routes so direct URL access redirects to overview.
  const visibleTabs = TABS.filter(
    (t) => (isPentester || !t.pentesterOnly) && (!t.onlyIf || t.onlyIf(pt)),
  );

  return (
    <div className="space-y-6">
      <HeroStrip
        icon={ClipboardCheck}
        accent="green"
        eyebrow={TYPE_LABELS[pt.type] || pt.type}
        title={pt.name}
        subtitle={
          <>
            Status: {pt.status} · Grade {pt.grade} ({pt.overall_score?.toFixed(2)})
            <GradeDelta original={pt.original_grade} current={pt.grade} />
            {' · '}{pt.finding_count} finding{pt.finding_count === 1 ? '' : 's'}
            {' · '}{pt.retest_count} retest{pt.retest_count === 1 ? '' : 's'}
          </>
        }
      />

      <nav className="flex flex-wrap gap-1 rounded-xl bg-cyber-100/60 p-1.5 border border-cyber-300/40 mx-6">
        {visibleTabs.map(({ path, label, icon: Icon }) => (
          <NavLink
            key={path}
            to={path}
            className={({ isActive }) =>
              `flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition ${
                isActive
                  ? 'bg-wasabi-100 text-wasabi-700 border border-wasabi-300'
                  : 'text-slate-700 hover:text-slate-900 hover:bg-cyber-200/50 border border-transparent'
              }`
            }
          >
            <Icon className="w-4 h-4" /> {label}
          </NavLink>
        ))}
      </nav>

      <div className="px-6">
        <Routes>
          <Route index element={<Navigate to="dashboards" replace />} />
          <Route path="dashboards" element={<Dashboards pentest={pt} />} />
          <Route path="pre-engagement" element={<PreEngagement pentest={pt} onUpdated={onUpdated} />} />
          {/* Legacy redirects so any bookmarked /overview or /questionnaire URLs land on the new page. */}
          <Route path="overview" element={<Navigate to="../pre-engagement" replace />} />
          <Route path="questionnaire" element={<Navigate to="../pre-engagement" replace />} />
          <Route
            path="tools/*"
            element={isPentester ? <Tools pentest={pt} /> : <Navigate to="../dashboards" replace />}
          />
          <Route path="runs" element={<Runs pentest={pt} />} />
          <Route path="runs/:kind/:runId" element={<RunDetail pentest={pt} />} />
          <Route path="findings" element={<Findings pentest={pt} onUpdated={onUpdated} />} />
          <Route path="ai-insights" element={<AIInsights pentest={pt} />} />
          <Route
            path="ai-pentest"
            element={isPentester ? <AIPentest pentest={pt} /> : <Navigate to="../dashboards" replace />}
          />
          <Route
            path="bench"
            element={isPentester ? <Bench pentest={pt} /> : <Navigate to="../dashboards" replace />}
          />
          <Route path="reports" element={<Reports pentest={pt} />} />
          <Route path="retests" element={<Retests pentest={pt} />} />
          <Route path="audit" element={<Audit pentest={pt} />} />
          <Route path="*" element={<Navigate to="dashboards" replace />} />
        </Routes>
      </div>
    </div>
  );
}
